/**
 * Audit Log for the new console (prototype v108 P 1350–1358; H§15): one history from audit_logs and admin_audit_log,
 * with search, User / Department / Action / Entity filters, summary tiles, Export CSV and Entity / Change / Before /
 * After / Source columns. Pure: rows in, what the page shows out.
 */
export const CATEGORIES = ['Configuration', 'Financial', 'Record Edit', 'Permission', 'Lifecycle', 'Flag', 'Other']
export const ENTITY_LABELS = { booking: 'Booking', client: 'Client', customer: 'Client', user: 'User', talent: 'Talent', photographer: 'Talent', flag: 'Flag', promotion: 'Promotion', coupon: 'Promotion', pricing: 'Business Control', business_control: 'Business Control', action_card: 'Action Card' }

const lower = v => String(v || '').toLowerCase()

/** Which kind of change an action is (the prototype's Action filter). */
export function categoryFor(actionType) {
  const a = lower(actionType)
  if (/flag/.test(a)) return 'Flag'
  if (/refund|payment|charge|invoice|deduction|credit|balance|paid|price_reduction|late_fee|complimentary/.test(a)) return 'Financial'
  if (/role|permission|invite|password|user_|member|access/.test(a)) return 'Permission'
  if (/pricing|price|rule|promotion|coupon|setting|config|control|package/.test(a)) return 'Configuration'
  if (/stage|lifecycle|cancel|delivered|approved|status|dates?_edited|rescheduled|complete/.test(a)) return 'Lifecycle'
  if (/edit|update|contact|note|blacklist|qbo|rating|assign/.test(a)) return 'Record Edit'
  return 'Other'
}

export function actionLabel(actionType) {
  return String(actionType || 'event').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const text = v => (v === null || v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v))

/** audit_logs row → view row. `roles` maps user id → department label. */
export function fromAuditLog(r, roles = new Map()) {
  return {
    id: `a:${r.id}`,
    at: r.created_at || null,
    user: r.performed_by_name || (r.user_id ? 'Staff' : 'System'),
    user_id: r.user_id || null,
    department: roles.get(r.user_id) || (r.user_id ? 'Staff' : 'System'),
    action_type: r.action_type || null,
    action: actionLabel(r.action_type),
    category: categoryFor(r.action_type),
    entity: ENTITY_LABELS[lower(r.entity_type)] || (r.entity_type ? actionLabel(r.entity_type) : '—'),
    entity_type: r.entity_type || null,
    entity_id: r.entity_id || null,
    change: text(r.details),
    before: text(r.old_values),
    after: text(r.new_values),
    source: r.user_id ? 'Staff console' : 'System / automation'
  }
}

/** admin_audit_log row (actor/subject joined) → view row. */
export function fromAdminAuditLog(r, roles = new Map()) {
  const p = r.payload || {}
  return {
    id: `b:${r.id}`,
    at: r.created_at || null,
    user: r.actor?.full_name || 'System',
    user_id: r.actor_id || null,
    department: roles.get(r.actor_id) || (r.actor_id ? 'Staff' : 'System'),
    action_type: r.action || null,
    action: actionLabel(r.action),
    category: categoryFor(r.action),
    entity: 'Talent',
    entity_type: 'talent',
    entity_id: p.talent_id || r.user_id || null,
    change: [p.talent_name || r.subject?.full_name, p.reason || p.notes].filter(Boolean).join(' · ') || text(p),
    before: text(p.previous_date ? { date: p.previous_date } : p.old_values || p.before),
    after: text(p.new_date ? { date: p.new_date } : p.new_values || p.after),
    source: 'Staff console'
  }
}

/**
 * booking_stage_history row → view row (Traci's doc, Oct 2026: "Add pipeline stage changes to audit log"). Every stage
 * change shows: a person's Update Stage (with their reason), Editing's approve / deliver, and the automatic ones.
 * `names` maps user id → name; `stageLabel(key)` gives the stage's name.
 */
export function fromStageHistory(r, roles = new Map(), names = new Map(), stageLabel = k => actionLabel(k)) {
  const manual = r.change_type === 'manual' || r.trigger_source === 'user'
  const by = r.changed_by ? names.get(r.changed_by) || 'Staff' : 'System'
  const reason = String(r.reason || '').replace(/^\[revert\]\s*/i, '')
  return {
    id: `s:${r.id}`,
    at: r.changed_at || null,
    user: by,
    user_id: r.changed_by || null,
    department: roles.get(r.changed_by) || (r.changed_by ? 'Staff' : 'System'),
    action_type: 'stage_changed',
    action: /^\[revert\]/i.test(String(r.reason || '')) ? 'Stage Reverted' : 'Stage Changed',
    category: 'Lifecycle',
    entity: 'Booking',
    entity_type: 'booking',
    entity_id: r.booking_id || null,
    change: [`${r.from_stage ? stageLabel(r.from_stage) : 'Start'} → ${stageLabel(r.to_stage)}`, reason].filter(Boolean).join(' · '),
    before: r.from_stage ? stageLabel(r.from_stage) : '',
    after: stageLabel(r.to_stage),
    source: manual ? 'Staff console' : `System / automation${r.trigger_source ? ` (${String(r.trigger_source).replace(/_/g, ' ')})` : ''}`
  }
}

/** @param f { q, user, department, category, entity, from, to } */
export function filterAudit(rows, f = {}) {
  const needle = lower(f.q).trim()
  return (rows || []).filter(r => {
    const day = String(r.at || '').slice(0, 10)
    if (f.from && day < f.from) return false
    if (f.to && day > f.to) return false
    if (f.user && r.user !== f.user) return false
    if (f.department && r.department !== f.department) return false
    if (f.category && r.category !== f.category) return false
    if (f.entity && r.entity !== f.entity) return false
    if (needle && ![r.user, r.action, r.entity, r.entity_id, r.change, r.before, r.after].some(v => lower(v).includes(needle))) return false
    return true
  })
}

export function auditSummary(rows) {
  const n = k => rows.filter(r => r.category === k).length
  return { total: rows.length, financial: n('Financial'), configuration: n('Configuration'), permission: n('Permission') }
}

const csvCell = v => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
export function auditCsv(rows) {
  const head = ['Date / Time', 'User', 'Department', 'Action', 'Category', 'Entity', 'Entity ID', 'Change', 'Before', 'After', 'Source']
  const lines = rows.map(r => [r.at, r.user, r.department, r.action, r.category, r.entity, r.entity_id, r.change, r.before, r.after, r.source].map(csvCell).join(','))
  return [head.join(','), ...lines].join('\n')
}
